import { useMessagingLive } from "../hooks/useMessagingLive";
import { MessageFiles, MessageUpload } from "../components/MessageFiles";
import type { MessageFile } from "../api/messaging.types";
import { useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bug,
  Link2,
  MessageSquare,
  Search,
  SquarePen,
} from "lucide-react";
import { http } from "../api/http";
import { useAuthStore } from "../store/auth";
import TeamView from "./TeamView";
import type { Conversation, Directory, Message } from "../api/messaging.types";
import {
  isRefusal,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
} from "../api/studentMessaging";
import BugReport from "../components/BugReport";
import ConversationList, { TEAM_LABELS } from "../components/ConversationList";
import NewMessageForm, { EMPTY_DRAFT } from "../components/NewMessageForm";
import {
  OFFLINE,
  dayLabel,
  initials,
  secondaryButton,
} from "../components/StudentMessageParts";
import StudentMessageThread from "../components/StudentMessageThread";
import {
  countUnread,
  shownStudentConversations,
  useStudentConversations,
} from "../hooks/useStudentConversations";
import { useShareTeamUnread } from "../hooks/useTeamUnread";
import { fmtTime, isSameDay } from "../lib/dates";
import { mergeConversations } from "../lib/unifiedMessaging";
import "../messaging.css";
const root = "/v1/messaging";
/** Une colonne, même sur grand écran : la liste, puis le fil à sa place. */
const frame = "messaging mx-auto max-w-3xl";
export default function MessagesView() {
  useMessagingLive();
  const [files, setFiles] = useState<MessageFile[]>([]),
    [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState<{
    q?: string;
    authorId?: string;
    entityType?: string;
    entityId?: string;
  }>({});
  const user = useAuthStore((s) => s.user),
    qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null),
    [text, setText] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [creating, setCreating] = useState(false),
    // Signalement d'un problème au service technique Permio : un écran, pas une conversation.
    [reporting, setReporting] = useState(false),
    // Recherche et onglets de la liste : un filtre d'affichage.
    [query, setQuery] = useState(""),
    [unreadOnly, setUnreadOnly] = useState(false),
    // Outils du fil de l'équipe, repliés tant qu'on ne les demande pas.
    [searchOpen, setSearchOpen] = useState(false),
    [linking, setLinking] = useState(false),
    [draft, setDraft] = useState(EMPTY_DRAFT),
    // Conversation avec un ou des élèves ouverte : une autre messagerie, un autre fil.
    [student, setStudent] = useState<string | null>(null),
    [before, setBefore] = useState<number | undefined>(),
    [editing, setEditing] = useState<Message | null>(null),
    [remove, setRemove] = useState<Message | null>(null),
    [object, setObject] = useState<{
      kind: "notes" | "meetings" | "boards";
      id: string;
    } | null>(null),
    [references, setReferences] = useState<
      { entityType: string; entityId: string }[]
    >([]),
    [link, setLink] = useState("");
  const pending = useRef({ signature: "", requestId: "" }),
    lock = useRef(false);
  const list = useQuery({
    queryKey: ["messaging", user?.id],
    queryFn: async () =>
      (
        await http.get<{ items: Conversation[]; unread: number }>(
          root + "/conversations",
        )
      ).data,
    refetchInterval: (q) => (q.state.error ? false : 5000),
    retry: false,
    gcTime: 0,
  });
  const directory = useQuery({
    queryKey: ["messaging-directory", user?.id],
    queryFn: async () => (await http.get<Directory>(root + "/directory")).data,
    retry: false,
    gcTime: 0,
  });
  const messages = useQuery({
    queryKey: ["messaging-messages", user?.id, selected, before, search],
    queryFn: async () =>
      (
        await http.get<{ items: Message[]; hasMore: boolean }>(
          `${root}/conversations/${selected}/messages`,
          { params: { before, ...search } },
        )
      ).data,
    enabled: !!selected,
    refetchInterval: (q) => (q.state.error ? false : 5000),
    retry: false,
    gcTime: 0,
  });
  const last = messages.data?.items[messages.data.items.length - 1]?.sequence;
  // Le fil défile dans son cadre et reste calé en bas, sauf si l'on est remonté lire.
  const timeline = useRef<HTMLDivElement>(null),
    followLatest = useRef(true);
  useLayoutEffect(() => {
    const el = timeline.current;
    if (el && followLatest.current) el.scrollTop = el.scrollHeight;
  }, [selected, last, messages.data]);
  // Les échanges avec les élèves viennent d'une autre messagerie : leur panne
  // ou leur refus ne doit rien retirer à celle de l'équipe.
  const students = useStudentConversations();
  const contacts = useQuery({
    queryKey: studentMessagingKeys.contacts(user?.id),
    queryFn: studentMessagingApi.getContacts,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  const teamDown = list.isError || directory.isError;
  // La pastille de la navigation reprend le total que cet écran affiche.
  useShareTeamUnread(teamDown ? 0 : list.data?.unread);
  async function command(path: string, payload: object, patch = false) {
    if (lock.current) return false;
    lock.current = true;
    setBusy(true);
    setError("");
    const signature = JSON.stringify([path, payload, patch]);
    if (pending.current.signature !== signature)
      pending.current = { signature, requestId: crypto.randomUUID() };
    try {
      await http[patch ? "patch" : "post"](root + path, {
        ...payload,
        requestId: pending.current.requestId,
      });
      pending.current = { signature: "", requestId: "" };
      await qc.invalidateQueries({ queryKey: ["messaging"] });
      await qc.invalidateQueries({ queryKey: ["messaging-messages"] });
      return true;
    } catch {
      setError(
        "Opération non confirmée. Actualisez et réessayez ; votre saisie est conservée.",
      );
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function send(e: FormEvent) {
    e.preventDefault();
    const content = {
      bodyVersion: 1,
      segments: [
        { kind: "text", text },
        ...references.map((reference) => ({ kind: "reference", reference })),
      ],
    };
    if (
      await command(
        `/conversations/${selected}/messages${editing ? "/" + editing.id : ""}`,
        {
          content,
          ...(editing
            ? { version: editing.version }
            : { fileIds: files.map((f) => f.id) }),
        },
        !!editing,
      )
    ) {
      setText("");
      setFiles([]);
      setReferences([]);
      setEditing(null);
      setBefore(undefined);
    }
  }
  function addLink() {
    try {
      const url = new URL(link);
      if (
        ![
          "permio.fr",
          "www.permio.fr",
          "app.permio.fr",
          window.location.hostname,
        ].includes(url.hostname) ||
        url.pathname !== "/workspace/reference"
      )
        throw new Error();
      const entityType = url.searchParams.get("type"),
        entityId = url.searchParams.get("id");
      if (
        !entityType ||
        !entityId ||
        !/^\w+$/.test(entityType) ||
        !/^[a-f0-9-]{36}$/i.test(entityId)
      )
        throw new Error();
      setReferences((r) =>
        r.some((v) => v.entityId === entityId && v.entityType === entityType)
          ? r
          : [...r, { entityId, entityType }],
      );
      setLink("");
    } catch {
      setError(
        "Collez un lien métier Permio. Son accès sera vérifié avant envoi.",
      );
    }
  }
  if (object)
    return (
      <section className="messaging space-y-4">
        <button
          className="messaging-button messaging-button--secondary"
          onClick={() => setObject(null)}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Retour aux messages
        </button>
        <TeamView initial={object} />
      </section>
    );
  const studentItems = shownStudentConversations(students);
  // Refus du serveur : rien n'est proposé. Panne passagère : ce qui est déjà connu reste utilisable.
  const access = isRefusal(contacts.error) ? undefined : contacts.data;
  const studentAccess =
    access?.canOpenDirect || access?.canCreateGroup ? access : undefined;
  const studentFailure = students.isError
    ? students.error
    : contacts.isError
      ? contacts.error
      : null;
  const studentsReachable = studentItems.length > 0 || !!studentAccess;
  const retryStudents = () => {
    void students.refetch();
    void contacts.refetch();
  };
  if (student) {
    const listed = studentItems.find((c) => c.id === student);
    return (
      <section className={frame}>
        <h1 className="sr-only">Messages</h1>
        <StudentMessageThread
          key={student}
          conversationId={student}
          fallbackTitle={listed?.title}
          fallbackKind={listed?.kind}
          onBack={() => setStudent(null)}
        />
      </section>
    );
  }
  const conversations = mergeConversations(
    teamDown ? [] : (list.data?.items ?? []),
    studentItems,
  );
  // Recherche et onglets ne filtrent que l'affichage : rien n'est redemandé au serveur.
  const needle = query.trim().toLocaleLowerCase();
  const shown = conversations.filter(
    (c) =>
      (!unreadOnly || c.unread > 0) &&
      c.title.toLocaleLowerCase().includes(needle),
  );
  const alert = (spacing: string) =>
    error && (
      <p role="alert" className={`messaging-alert ${spacing}`}>
        {error}
      </p>
    );
  if (selected && !reporting && !teamDown) {
    const opened = list.data?.items.find((c) => c.id === selected);
    const items = messages.data?.items ?? [];
    return (
      <section className={frame}>
        <h1 className="sr-only">Messages</h1>
        <div className="messaging-card messaging-conversation h-[calc(100dvh-11rem)] lg:h-[calc(100dvh-9rem)] min-h-[28rem]">
          <header>
            <button
              type="button"
              className="messaging-back"
              aria-label="Retour aux conversations"
              title="Retour aux conversations"
              onClick={() => {
                setSearch({});
                setFiles([]);
                setSelected(null);
                setBefore(undefined);
                setText("");
                setEditing(null);
                setReferences([]);
                setSearchOpen(false);
                setLinking(false);
              }}
            >
              <ArrowLeft size={18} aria-hidden="true" />
            </button>
            <div>
              <h2>{opened?.title ?? "Conversation"}</h2>
              <p>{TEAM_LABELS[opened?.type ?? ""] ?? "Équipe"}</p>
            </div>
          </header>
          {messages.isError ? (
            <div className="messaging-empty">
              <p role="alert" className="messaging-alert">
                Conversation indisponible. Le contenu est masqué.
              </p>
              <button
                className={secondaryButton}
                onClick={() => void messages.refetch()}
              >
                Réessayer
              </button>
            </div>
          ) : (
            <>
              <div className="messaging-conversation-toolbar">
                <button
                  type="button"
                  className="messaging-ghost"
                  aria-expanded={searchOpen}
                  onClick={() => setSearchOpen(!searchOpen)}
                >
                  <Search size={14} aria-hidden="true" />
                  Rechercher
                </button>
                {!before && !Object.values(search).some(Boolean) && !!last && (
                  <button
                    className="messaging-ghost"
                    onClick={() =>
                      void http
                        .post(`${root}/conversations/${selected}/read`, {
                          sequence: last,
                        })
                        .then(() =>
                          qc.invalidateQueries({ queryKey: ["messaging"] }),
                        )
                        .catch(() => setError("Lecture non confirmée."))
                    }
                  >
                    Marquer comme lu
                  </button>
                )}
                {before && (
                  <button
                    className="messaging-ghost"
                    onClick={() => setBefore(undefined)}
                  >
                    Derniers messages
                  </button>
                )}
                {messages.data?.hasMore && (
                  <button
                    className="messaging-ghost"
                    onClick={() => setBefore(messages.data!.items[0].sequence)}
                  >
                    Messages précédents
                  </button>
                )}
              </div>
              {searchOpen && (
                <form
                  className="messaging-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    const url = String(f.get("reference") || "").trim();
                    let ref = {};
                    if (url) {
                      try {
                        const u = new URL(url);
                        if (
                          ![
                            "permio.fr",
                            "www.permio.fr",
                            "app.permio.fr",
                            window.location.hostname,
                          ].includes(u.hostname) ||
                          u.pathname !== "/workspace/reference"
                        )
                          throw new Error();
                        ref = {
                          entityType: u.searchParams.get("type") || "",
                          entityId: u.searchParams.get("id") || "",
                        };
                      } catch {
                        setError("Lien métier invalide.");
                        return;
                      }
                    }
                    setSearch({
                      q: String(f.get("q") || "").trim() || undefined,
                      authorId: String(f.get("author") || "") || undefined,
                      ...ref,
                    });
                    setBefore(undefined);
                  }}
                >
                  <label>
                    Rechercher dans la conversation
                    <input
                      name="q"
                      minLength={2}
                      maxLength={120}
                      className="messaging-field"
                    />
                  </label>
                  <label>
                    Auteur
                    <select name="author" className="messaging-field">
                      <option value="">Tous les auteurs</option>
                      {directory.data?.items.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Lien de l’objet recherché (facultatif)
                    <input name="reference" className="messaging-field" />
                  </label>
                  <div className="messaging-form-actions">
                    <button
                      type="button"
                      className={secondaryButton}
                      onClick={() => {
                        setSearch({});
                        setBefore(undefined);
                        setSearchOpen(false);
                      }}
                    >
                      Effacer la recherche
                    </button>
                    <button className="messaging-button messaging-button--small">
                      Rechercher l’historique
                    </button>
                  </div>
                </form>
              )}
              <div
                ref={timeline}
                className="messaging-log"
                onScroll={(e) => {
                  const el = e.currentTarget;
                  followLatest.current =
                    el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                }}
              >
                {messages.isPending && (
                  <p className="messaging-empty">Chargement…</p>
                )}
                {!!messages.data && items.length === 0 && (
                  <p className="messaging-empty">
                    {Object.values(search).some(Boolean)
                      ? "Aucun message ne correspond à la recherche."
                      : "Aucun message pour le moment. Écrivez le premier."}
                  </p>
                )}
                <ol>
                  {items.map((m, index) => (
                    <li
                      key={m.id}
                      className={`messaging-message${
                        m.authorId === user?.id ? " messaging-message--mine" : ""
                      }`}
                    >
                      {(index === 0 ||
                        !isSameDay(
                          items[index - 1].createdAt,
                          m.createdAt,
                        )) && (
                        <div className="messaging-day">
                          {dayLabel(m.createdAt)}
                        </div>
                      )}
                      <div className="messaging-author-line">
                        <span
                          className="messaging-message-avatar"
                          aria-hidden="true"
                        >
                          {initials(m.authorName)}
                        </span>
                        <strong>{m.authorName}</strong>
                        <time
                          dateTime={m.createdAt}
                          title={new Date(m.createdAt).toLocaleString("fr-FR")}
                        >
                          {fmtTime(m.createdAt)}
                        </time>
                        {m.editedAt && <small>Modifié</small>}
                      </div>
                      {m.deletedAt ? (
                        <p className="messaging-muted">Message supprimé</p>
                      ) : (
                        <>
                          <p className="messaging-text">
                            {m.content?.segments
                              .flatMap((s) =>
                                s.kind === "text" ? [s.text] : [],
                              )
                              .join("\n")}
                          </p>
                          <MessageFiles
                            conversation={selected}
                            files={m.files ?? []}
                            onError={setError}
                          />
                          {m.references.map((r, i) => {
                            const kind = (
                              {
                                NOTE: "notes",
                                MEETING: "meetings",
                                BOARD: "boards",
                              } as const
                            )[r.entityType as "NOTE" | "MEETING" | "BOARD"];
                            return (
                              <div className="messaging-reference" key={i}>
                                {r.available ? (
                                  <>
                                    <strong>{r.label}</strong>
                                    {r.fields?.map((f) => (
                                      <p key={f.label}>
                                        {f.label} : {f.value}
                                      </p>
                                    ))}
                                    {kind && (
                                      <button
                                        onClick={() =>
                                          setObject({ kind, id: r.entityId })
                                        }
                                      >
                                        Ouvrir l’objet
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  "Élément Permio · Vous n’avez pas accès à cet élément."
                                )}
                              </div>
                            );
                          })}
                          <div className="messaging-message-actions">
                            {["👍", "✅", "👀"].map((emoji) => (
                              <button
                                key={emoji}
                                disabled={busy}
                                aria-label={"Réagir " + emoji}
                                aria-pressed={m.reactions.some(
                                  (r) => r.emoji === emoji && r.mine,
                                )}
                                onClick={() =>
                                  void command(
                                    `/conversations/${selected}/messages/${m.id}/reactions`,
                                    {
                                      emoji,
                                      active: !m.reactions.some(
                                        (r) => r.emoji === emoji && r.mine,
                                      ),
                                    },
                                  )
                                }
                              >
                                {emoji}{" "}
                                {m.reactions.find((r) => r.emoji === emoji)
                                  ?.count || ""}
                              </button>
                            ))}
                            {m.authorId === user?.id && (
                              <>
                                {Date.now() - Date.parse(m.createdAt) <
                                  900000 && (
                                  <button
                                    onClick={() => {
                                      setFiles([]);
                                      setEditing(m);
                                      setText(
                                        m
                                          .content!.segments.flatMap((s) =>
                                            s.kind === "text" ? [s.text] : [],
                                          )
                                          .join("\n"),
                                      );
                                      setReferences(
                                        m.content!.segments.flatMap((s) =>
                                          s.kind === "reference"
                                            ? [s.reference]
                                            : [],
                                        ),
                                      );
                                    }}
                                  >
                                    Modifier
                                  </button>
                                )}
                                <button onClick={() => setRemove(m)}>
                                  Supprimer
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
              {remove && (
                <div className="messaging-form">
                  <p>
                    Masquer le message ? Une trace historique est conservée.
                  </p>
                  <div className="messaging-form-actions">
                    <button
                      className={secondaryButton}
                      onClick={() => setRemove(null)}
                    >
                      Annuler
                    </button>
                    <button
                      disabled={busy}
                      className="messaging-button messaging-button--small"
                      onClick={() =>
                        void command(
                          `/conversations/${selected}/messages/${remove.id}/delete`,
                          { version: remove.version },
                        ).then((ok) => {
                          if (ok) setRemove(null);
                        })
                      }
                    >
                      Confirmer la suppression
                    </button>
                  </div>
                </div>
              )}
              {alert("px-4 pt-2")}
              <form onSubmit={send} className="messaging-composer">
                <textarea
                  aria-label="Votre message"
                  placeholder="Rédigez un message…"
                  rows={3}
                  required={
                    !references.length &&
                    !files.length &&
                    !editing?.files?.length
                  }
                  maxLength={20000}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={busy}
                />
                {references.map((r, i) => (
                  <span key={i} className="messaging-chip">
                    Objet métier · {r.entityType}
                    <button
                      type="button"
                      onClick={() =>
                        setReferences(references.filter((_, n) => n !== i))
                      }
                    >
                      Retirer
                    </button>
                  </span>
                ))}
                {linking && (
                  <div className="messaging-form mt-3">
                    <label>
                      Lien métier Permio
                      <input
                        className="messaging-field"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                      />
                    </label>
                    <div className="messaging-form-actions">
                      <button
                        type="button"
                        className={secondaryButton}
                        disabled={!link || references.length >= 50 || busy}
                        onClick={addLink}
                      >
                        Joindre l’objet
                      </button>
                    </div>
                  </div>
                )}
                <div className="messaging-composer-tools">
                  {!editing && (
                    <MessageUpload
                      conversation={selected}
                      files={files}
                      onFiles={setFiles}
                      onBusy={setUploading}
                      onError={setError}
                      disabled={busy}
                    />
                  )}
                  <button
                    type="button"
                    className="messaging-ghost"
                    aria-expanded={linking}
                    onClick={() => setLinking(!linking)}
                  >
                    <Link2 size={15} aria-hidden="true" />
                    Lier un objet
                  </button>
                  {editing && (
                    <button
                      type="button"
                      className="messaging-ghost"
                      onClick={() => {
                        setEditing(null);
                        setText("");
                        setReferences([]);
                      }}
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    className="messaging-send"
                    disabled={
                      busy ||
                      uploading ||
                      (!text.trim() &&
                        !references.length &&
                        !files.length &&
                        !editing?.files?.length)
                    }
                  >
                    {editing ? "Enregistrer le message" : "Envoyer"}
                  </button>
                </div>
              </form>
            </>
          )}
          {messages.isError && alert("px-4 pb-3")}
        </div>
      </section>
    );
  }
  // Une seule liste et un seul « Nouveau message » pour les deux messageries.
  const listed = !teamDown || studentsReachable;
  return (
    <section className={`${frame} space-y-5`}>
      <header className="messaging-page-heading">
        <h1>Messages</h1>
        <p>Une seule messagerie pour vos collègues et vos élèves.</p>
      </header>
      <div className="messaging-card messaging-pane">
        {reporting ? (
          // Accessible même quand la messagerie est en panne : c'est là qu'on en a besoin.
          <BugReport onClose={() => setReporting(false)} />
        ) : (
          <>
            <div className="messaging-inbox-heading">
              <div>
                <h2>Conversations</h2>
                {listed && (
                  <p>
                    {(teamDown ? 0 : (list.data?.unread ?? 0)) +
                      countUnread(studentItems)}{" "}
                    message(s) non lu(s)
                  </p>
                )}
              </div>
              <button
                type="button"
                className="messaging-icon-button messaging-icon-button--report"
                aria-label="Signaler un problème"
                title="Signaler un problème au service technique Permio"
                onClick={() => setReporting(true)}
              >
                <Bug size={18} aria-hidden="true" />
              </button>
              {listed && (
                <button
                  type="button"
                  className="messaging-icon-button"
                  aria-label="Nouveau message"
                  title="Nouveau message"
                  aria-expanded={creating}
                  onClick={() => setCreating(!creating)}
                >
                  <SquarePen size={18} aria-hidden="true" />
                </button>
              )}
            </div>
            {teamDown && (
              <div className="messaging-outcome messaging-outcome--blocked mb-3 space-y-2">
                <p role="alert">
                  {studentsReachable
                    ? "Messagerie de l’équipe indisponible ou accès retiré. Les échanges avec les élèves restent accessibles."
                    : "Messagerie indisponible ou accès retiré."}
                </p>
                <button
                  className={secondaryButton}
                  onClick={() => {
                    void list.refetch();
                    void directory.refetch();
                    if (studentFailure) retryStudents();
                  }}
                >
                  Réessayer
                </button>
              </div>
            )}
            {listed && (
              <>
                {creating && (
                  <NewMessageForm
                    draft={draft}
                    onDraft={setDraft}
                    me={user?.id}
                    directory={teamDown ? undefined : directory.data}
                    refreshing={directory.isFetching}
                    onRefresh={() => {
                      void directory.refetch();
                      if (studentAccess) void contacts.refetch();
                    }}
                    busy={busy}
                    studentAccess={studentAccess}
                    onCreateTeam={(conversation) =>
                      command("/conversations", conversation)
                    }
                    onTeamCreated={() => setCreating(false)}
                    onStudentConversation={(id) => {
                      void qc.invalidateQueries({
                        queryKey: studentMessagingKeys.conversations(user?.id),
                      });
                      setCreating(false);
                      setStudent(id);
                    }}
                  />
                )}
                {conversations.length > 0 && (
                  <>
                    <label className="messaging-inbox-search">
                      <Search size={17} aria-hidden="true" />
                      <input
                        type="search"
                        aria-label="Rechercher une conversation"
                        placeholder="Rechercher une conversation"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </label>
                    <div
                      className="messaging-inbox-tabs"
                      role="group"
                      aria-label="Afficher les conversations"
                    >
                      <button
                        type="button"
                        aria-pressed={!unreadOnly}
                        onClick={() => setUnreadOnly(false)}
                      >
                        Toutes
                      </button>
                      <button
                        type="button"
                        aria-pressed={unreadOnly}
                        onClick={() => setUnreadOnly(true)}
                      >
                        Non lues
                      </button>
                    </div>
                  </>
                )}
                <ConversationList
                  items={shown}
                  onOpen={(c) => {
                    if (c.origin === "student") {
                      setStudent(c.id);
                      return;
                    }
                    followLatest.current = true;
                    setSelected(c.id);
                  }}
                />
                {conversations.length > 0 && shown.length === 0 && (
                  <p className="messaging-empty">
                    Aucune conversation correspondante.
                  </p>
                )}
                {conversations.length === 0 &&
                  (teamDown || !!list.data) &&
                  !students.isPending && (
                    <div className="messaging-empty">
                      <span className="messaging-symbol" aria-hidden="true">
                        <MessageSquare size={26} />
                      </span>
                      <strong>Aucune conversation.</strong>
                      <span>
                        Le crayon, en haut à droite, ouvre un nouveau message.
                      </span>
                    </div>
                  )}
                {!teamDown && studentFailure && (
                  <p role="status" className="messaging-hint mt-3">
                    Échanges avec les élèves :{" "}
                    {studentMessagingError(studentFailure, OFFLINE)}{" "}
                    <button type="button" onClick={retryStudents}>
                      Réessayer
                    </button>
                  </p>
                )}
              </>
            )}
            {alert("mt-3")}
          </>
        )}
      </div>
    </section>
  );
}
